import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { listRedemptions, promoErrorMessage, type PromoCode, type PromoRedemption } from '@/lib/promoApi';

type Props = {
  walletAddress: string;
  code: PromoCode | null;
  onClose: () => void;
};

export const PromoCodeRedemptions: React.FC<Props> = ({ walletAddress, code, onClose }) => {
  const [rows, setRows] = useState<PromoRedemption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRows([]);
    listRedemptions(walletAddress, code.promoCodeId)
      .then((data) => {
        if (cancelled) return;
        setRows(data.sort((a, b) => b.redeemedAt - a.redeemedAt));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(promoErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [code, walletAddress]);

  return (
    <Dialog open={!!code} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid='promo-redemptions-dialog'
        className='max-w-2xl bg-[var(--pp-surface)] text-[var(--pp-text)]'
      >
        <DialogHeader>
          <DialogTitle>
            Redemptions · <span className='font-mono'>{code?.code}</span>
          </DialogTitle>
          <DialogDescription>
            Each row is one settled session that applied this code. Counts are the audit source of truth.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className='flex items-center justify-center gap-2 py-10 text-sm' style={{ color: 'var(--pp-text-subtle)' }}>
            <Loader2 size={16} className='animate-spin' />
            Loading redemptions…
          </div>
        ) : error ? (
          <div className='py-8 text-center text-sm' style={{ color: 'var(--pp-danger)' }}>{error}</div>
        ) : rows.length === 0 ? (
          <div className='py-10 text-center text-sm' style={{ color: 'var(--pp-text-subtle)' }}>
            No redemptions yet.
          </div>
        ) : (
          <div className='overflow-x-auto rounded-lg border' style={{ borderColor: 'var(--pp-border)' }}>
            <table className='w-full text-sm'>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--pp-border)', background: 'var(--pp-subtle)' }}>
                  {['Session', 'User', 'Original', 'Charged', 'Off', 'Redeemed'].map((h) => (
                    <th
                      key={h}
                      className='text-left px-3 py-2 text-xs font-medium uppercase tracking-wide'
                      style={{ color: 'var(--pp-text-subtle)' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.sessionId}
                    data-testid='promo-redemption-row'
                    style={{
                      borderBottom: i < rows.length - 1 ? '1px solid var(--pp-border)' : 'none',
                      background: i % 2 === 0 ? 'var(--pp-surface)' : 'var(--pp-bg)',
                    }}
                  >
                    <td className='px-3 py-2' style={{ fontFamily: 'var(--pp-mono)', fontSize: '0.72rem', color: 'var(--pp-text)' }}>
                      {r.sessionId.slice(0, 16)}…
                    </td>
                    <td className='px-3 py-2' style={{ color: 'var(--pp-text-muted)' }}>{r.tenantUserId}</td>
                    <td className='px-3 py-2 tabular-nums' style={{ color: 'var(--pp-text-subtle)', textDecoration: 'line-through' }}>
                      ${(r.originalAmountUsdCents / 100).toFixed(2)}
                    </td>
                    <td className='px-3 py-2 tabular-nums' style={{ color: 'var(--pp-text)', fontWeight: 500 }}>
                      ${(r.chargedAmountUsdCents / 100).toFixed(2)}
                    </td>
                    <td className='px-3 py-2' style={{ color: 'var(--pp-text-muted)' }}>{r.percentOff}%</td>
                    <td className='px-3 py-2' style={{ fontSize: '0.72rem', color: 'var(--pp-text-subtle)' }}>
                      {new Date(r.redeemedAt * 1000).toLocaleString([], {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PromoCodeRedemptions;
